import endpoints from "../../../../utils/endpoints";
import type { FlowStep } from "../types";
import { FlowStepError, StepSkippedError } from "../engine/errors";
import { finalEstimateStatesOn, requireEnquiry, waitForSnapshot } from "./common";

export const STEP_PART_CONFIRMATION_REQUEST = "part_confirmation_request";
export const STEP_PART_CONFIRMATION_RESPOND = "part_confirmation_respond";

const adoptFromSnapshot = (rt: Parameters<FlowStep["execute"]>[0]) => {
  const requestId = rt.ctx.snapshot?.partConfirmation?.requestId;
  if (!requestId) return false;
  rt.ctx.partConfirmation = {
    requestId,
    supplierId: rt.ctx.snapshot?.partConfirmation?.supplierId,
  };
  return true;
};

export const partConfirmationRequestStep: FlowStep = {
  id: STEP_PART_CONFIRMATION_REQUEST,
  name: "Request part confirmation",
  targetLabel: "Part confirmation requested",
  description:
    "Adopts the part confirmation the backend sends after part conversion ends (IAM supplier). Falls back to POST /v1/part-confirmations if none appears. Skipped when the supplier is not eligible.",
  actor: "ORCA",
  kind: "api",
  optional: true,
  estimateMs: [800, 5000],
  shouldSkip: (ctx) =>
    finalEstimateStatesOn(ctx)
      ? "Final estimate states are on: part confirmations are sent automatically once the enquiry is FINAL_ESTIMATE_REQUESTED"
      : null,
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);

    if (adoptFromSnapshot(rt)) {
      rt.log(
        `Part confirmation ${rt.ctx.partConfirmation!.requestId} already sent by backend after part conversion end`,
        "success"
      );
      return;
    }

    if (rt.ctx.snapshot?.enquiry?.partConversion === "COMPLETED") {
      try {
        await waitForSnapshot(rt, {
          trigger: "IAM part confirmation after part conversion end",
          expected: "A part confirmation request for this enquiry",
          check: (s) => Boolean(s.partConfirmation?.requestId),
          describe: (s) =>
            s.partConfirmation?.requestId
              ? `requestId=${s.partConfirmation.requestId}`
              : "no request yet",
          maxAttempts: 1,
          scope: { enquiry: true, partConfirmation: true },
        });
      } catch {
        // Auto-send is fire-and-forget and may skip (no IAM area / not eligible).
      }
    }

    if (adoptFromSnapshot(rt)) {
      rt.log(
        `Part confirmation ${rt.ctx.partConfirmation!.requestId} already sent by backend after part conversion end`,
        "success"
      );
      return;
    }

    const items = rt.ctx.snapshot?.enquiry?.orderItems || [];
    const parts = rt.ctx.config.parts.map((cfg) => {
      const live = items.find((i) => i.partId === cfg.partId);
      return {
        partId: cfg.partId,
        partNumber: cfg.partNumber || live?.partNumber || cfg.partId,
        rootPartNumber: cfg.rootPartNumber || live?.rootPartNumber || cfg.partNumber || cfg.partId,
        partName: cfg.partName || live?.partName,
        askedQty: cfg.quantity,
        stockQty: 0,
      };
    });

    try {
      const res = await rt.api<{ requestId: string; notified: boolean }>({
        label: "Create part confirmation request",
        method: "POST",
        path: endpoints.createPartConfirmation,
        body: {
          orderId: enquiry.orderId,
          orderNo: enquiry.orderNo,
          supplierId: rt.ctx.supplier.id,
          parts,
        },
      });
      if (!res.data?.requestId) {
        throw new FlowStepError("Part confirmation response has no requestId", {
          apiCall: res.record,
        });
      }
      rt.ctx.partConfirmation = { requestId: res.data.requestId, supplierId: rt.ctx.supplier.id };
      rt.log(
        `Part confirmation request ${res.data.requestId} created (notified=${res.data.notified})`,
        "success"
      );
    } catch (err: any) {
      if (err?.apiCall?.status === 403) {
        throw new StepSkippedError(
          "Supplier is not enabled for part confirmation (part_confirmation_eligible_supplier_ids); continuing without it."
        );
      }
      throw err;
    }
  },
};

export const partConfirmationRespondStep: FlowStep = {
  id: STEP_PART_CONFIRMATION_RESPOND,
  name: "Supplier confirms parts",
  targetLabel: "Parts confirmed by supplier",
  description:
    "Supplier confirms each requested part (POST /v1/part-confirmations/:requestId/responses with the supplier token).",
  actor: "SUPPLIER",
  kind: "api",
  optional: true,
  estimateMs: [2000, 8000],
  shouldSkip: (ctx) => {
    if (finalEstimateStatesOn(ctx)) {
      return "Final estimate states are on: part confirmations are handled automatically";
    }
    const requestId = ctx.partConfirmation?.requestId || ctx.snapshot?.partConfirmation?.requestId;
    if (!requestId) return "No part confirmation request exists for this enquiry";
    const requestSupplier = ctx.partConfirmation?.supplierId || ctx.snapshot?.partConfirmation?.supplierId;
    if (requestSupplier && requestSupplier !== ctx.supplier.id) {
      return `Auto part confirmation was sent to supplier ${requestSupplier}, not the configured ${ctx.supplier.name}`;
    }
    return null;
  },
  execute: async (rt) => {
    if (!rt.ctx.partConfirmation?.requestId) adoptFromSnapshot(rt);
    const { requestId } = rt.ctx.partConfirmation!;
    const token = await rt.getSupplierToken();
    const items = rt.ctx.snapshot?.enquiry?.orderItems || [];

    for (const cfg of rt.ctx.config.parts) {
      const live = items.find((i) => i.partId === cfg.partId);
      await rt.api({
        label: `Confirm part ${cfg.partNumber || cfg.partId}`,
        method: "POST",
        path: `/v1/part-confirmations/${encodeURIComponent(requestId)}/responses`,
        body: {
          partId: cfg.partId,
          partNumber: cfg.partNumber || live?.partNumber || cfg.partId,
          rootPartNumber:
            cfg.rootPartNumber || live?.rootPartNumber || cfg.partNumber || cfg.partId,
          quantity: cfg.quantity,
          status: "confirmed",
        },
        actor: "SUPPLIER",
        authToken: token,
      });
    }

    await waitForSnapshot(rt, {
      trigger: "Supplier part confirmation responses",
      expected: "All requested parts answered",
      check: (s) => Boolean(s.partConfirmation?.responded),
      describe: (s) =>
        `partConfirmation=${s.partConfirmation?.responded ? "responded" : "pending"} (${
          s.partConfirmation?.confirmedParts ?? 0
        }/${s.partConfirmation?.totalParts ?? 0})`,
      maxAttempts: 3,
      scope: { enquiry: true, partConfirmation: true },
    });
  },
  verify: (s) => ({
    ok: Boolean(s.partConfirmation?.responded),
    expected: "partConfirmation.responded = true",
    actual: `partConfirmation.responded = ${Boolean(s.partConfirmation?.responded)}`,
  }),
};
